import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import TagManager from 'react-gtm-module';

import { DATA_LAYER_NAME } from '@/common/constants';
import CookiesAnalytics from '@/common/services/Cookies.analytics';

import { useAuth } from './AuthContext';
import { useConfig } from './ConfigContext';

const TagManagerContext = createContext(null);

export const TagManagerContextProvider = ({ children }) => {
  const { user } = useAuth();
  const { env } = useConfig();

  const defaultTagManagerArgs = useMemo(
    () => ({
      company: user?.company,
      customer_id: user?.customer_code,
    }),
    [user],
  );

  /**
   * Reset dataLayer if it already exists
   * @type {(...args: any[]) => any}
   */
  const handleResetDataLayer = useCallback(() => {
    const gtm =
      window.google_tag_manager &&
      window.google_tag_manager[
        process.env[`REACT_APP_GOOGLE_TAG_MANAGER_ID_${env.toUpper}`]
      ];

    if (gtm) {
      gtm.dataLayer.reset();
    }
  }, [env]);

  /**
   * Push dataLayer for cloud retail
   * @param {object} dataLayer
   */
  const onPushCloudRetail = useCallback(
    (dataLayer) => {
      handleResetDataLayer();

      TagManager.dataLayer({
        dataLayer: {
          ...defaultTagManagerArgs,
          ...dataLayer,
          cloud_retail: {
            ...dataLayer.cloud_retail,
            visitorId: CookiesAnalytics.getCookie('_ga'),
            userInfo: {
              userId: `${user?.company_code}${user?.customer_code}`,
            },
          },
        },
        dataLayerName: DATA_LAYER_NAME,
      });
    },
    [defaultTagManagerArgs, handleResetDataLayer, user],
  );

  /**
   * Push dataLayer for tag manager event
   * @param {object} dataLayer
   */
  const onPushEvent = useCallback(
    (dataLayer) => {
      handleResetDataLayer();

      TagManager.dataLayer({
        dataLayer: {
          ...defaultTagManagerArgs,
          ...dataLayer,
        },
        dataLayerName: DATA_LAYER_NAME,
      });
    },
    [defaultTagManagerArgs, handleResetDataLayer],
  );

  return (
    <TagManagerContext.Provider value={{ onPushCloudRetail, onPushEvent }}>
      {children}
    </TagManagerContext.Provider>
  );
};

export const useTagManager = () => useContext(TagManagerContext);

TagManagerContextProvider.defaultProps = {};

TagManagerContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
